/**
 * @license
 * Copyright 2023 Tyler Technologies, Inc. 
 * License: Apache-2.0
 */
import { addClass, getShadowElement, removeClass, removeElement, walkUpUntil, elementFromHTML, toggleClass } from '@tylertech/forge-core';
import { BaseAdapter } from '../../core/base/base-adapter';
import { CHIP_SET_CONSTANTS } from '../chip-set/chip-set-constants';
import { CHIP_CONSTANTS } from './chip-constants';
import { ICON_CONSTANTS } from '../../icon';
import { ForgeRipple } from '../../ripple';
const checkmarkTemplate = '<div class=\"forge-chip__checkmark\" aria-hidden=\"true\"><svg class=\"forge-chip__checkmark-svg\" viewBox=\"-2 -3 30 30\"><path class=\"forge-chip__checkmark-path\" fill=\"none\" stroke=\"black\" d=\"M1.73,12.91 8.1,19.28 22.79,4.59\"/></svg></div>';
export class ChipAdapter extends BaseAdapter {
    constructor(component) {
        super(component);
        this._buttonElement = getShadowElement(this._component, CHIP_CONSTANTS.selectors.BUTTON);
        this._leadingSlotElement = getShadowElement(this._component, 'slot[name=leading]');
    }
    addButtonListener(type, listener) {
        this._buttonElement.addEventListener(type, listener);
    }
    removeButtonListener(type, listener) {
        this._buttonElement.removeEventListener(type, listener);
    }
    initializeRipple() {
        return new ForgeRipple(this._buttonElement);
    }
    clearTypeClass() {
        removeClass([CHIP_CONSTANTS.classes.ACTION, CHIP_CONSTANTS.classes.CHOICE, CHIP_CONSTANTS.classes.FILTER, CHIP_CONSTANTS.classes.INPUT], this._buttonElement);
    }
    addRootClass(className) {
        this._buttonElement.classList.add(className);
    }
    removeRootClass(name) {
        this._buttonElement.classList.remove(name);
    }
    setCheckmarkVisibility(isVisible) {
        if (isVisible) {
            if (!this._checkmarkElement) {
                this._checkmarkElement = elementFromHTML(checkmarkTemplate);
            }
            this._buttonElement.insertBefore(this._checkmarkElement, this._buttonElement.firstChild);
        }
        else if (this._checkmarkElement && this._checkmarkElement.isConnected) {
            removeElement(this._checkmarkElement);
        }
    }
    setSelected(value) {
        toggleClass(this._buttonElement, value, CHIP_CONSTANTS.classes.SELECTED);
    }
    setDisabled(value) {
        this._buttonElement.disabled = value;
    }
    setDense(value) {
        toggleClass(this._buttonElement, value, CHIP_CONSTANTS.classes.DENSE);
    }
    setDeleteButtonVisibility(isVisible, listener) {
        if (isVisible) {
            if (!this._deleteButton) {
                this._deleteButton = this._createDeleteButton(listener);
            }
            if (!this._deleteButtonTouchTarget) {
                this._deleteButtonTouchTarget = this._createDeleteButtonTouchTarget(listener);
            }
            this._deleteButton.appendChild(this._deleteButtonTouchTarget);
            this._buttonElement.appendChild(this._deleteButton);
        }
        else {
            if (this._deleteButtonTouchTarget) {
                removeElement(this._deleteButtonTouchTarget);
            }
            if (this._deleteButton) {
                removeElement(this._deleteButton);
            }
        }
    }
    setLeadingSlotVisibility(isVisible) {
        toggleClass(this._leadingSlotElement, !isVisible, CHIP_CONSTANTS.classes.LEADING_HIDDEN);
    }
    getChipSetState() {
        let state = null;
        const chipSet = walkUpUntil(this._component, node => node && node.nodeName === CHIP_SET_CONSTANTS.elementName.toUpperCase());
        if (chipSet) {
            state = {
                type: chipSet.type,
                disabled: chipSet.disabled,
                dense: chipSet.dense
            };
        }
        return state;
    }
    _createDeleteButton(listener) {
        const el = document.createElement(ICON_CONSTANTS.elementName);
        el.name = 'cancel';
        el.tabIndex = 0;
        el.setAttribute('aria-label', 'Delete');
        el.setAttribute('role', 'button');
        addClass([CHIP_CONSTANTS.classes.DELETE_BUTTON], el);
        el.addEventListener('keydown', listener);
        return el;
    }
    _createDeleteButtonTouchTarget(listener) {
        const el = document.createElement('div');
        addClass(CHIP_CONSTANTS.classes.DELETE_BUTTON_TOUCH_TARGET, el);
        el.addEventListener('keydown', listener);
        el.addEventListener('click', listener);
        return el;
    }
    setFocus() {
        this._buttonElement.focus();
    }
    setEmulatedFocus(value) {
        this._buttonElement.classList.toggle('mdc-ripple-upgraded--background-focused', value);
    }
}
